import {
  ConditionGroup,
  AnswerOptionCondition,
  AnswerOptionConditionType,
  isConditionGroup,
} from 'shared/types/due-diligence';

/**
 * Generate a unique ID for conditions and groups
 */
export const generateId = (): string => {
  try {
    return `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  } catch (error) {
    console.error('Error generating condition ID:', error);
    throw new Error('Kon geen unieke ID genereren voor conditie');
  }
};

/**
 * Create a new condition group with default logic
 */
export function createNewGroup(logic: 'AND' | 'OR' = 'AND'): ConditionGroup {
  try {
    return {
      id: generateId(),
      type: 'group',
      logic,
      children: [],
    };
  } catch (error) {
    console.error('Error creating new group:', error);
    throw new Error('Kon geen nieuwe conditiegroep aanmaken');
  }
}

/**
 * Create a new condition with type-specific defaults
 */
export function createNewCondition(
  type: AnswerOptionConditionType
): AnswerOptionCondition {
  try {
    const baseCondition: AnswerOptionCondition = {
      id: generateId(),
      type,
    };

    switch (type) {
      case AnswerOptionConditionType.FILE_EXISTS:
        return {
          ...baseCondition,
          fileExists: true,
        };

      case AnswerOptionConditionType.EXPIRATION_DATE:
        return {
          ...baseCondition,
          expirationComparison: 'more_than_days',
          expirationDays: 30,
        };

      case AnswerOptionConditionType.FIELD_VALUE:
        return {
          ...baseCondition,
          field: 'status',
          operator: '==',
          value: 'Ok',
        };

      case AnswerOptionConditionType.AI_QUESTION:
        return {
          ...baseCondition,
          aiQuestion: '',
          expectedAnswer: 'yes',
        };

      case AnswerOptionConditionType.AI_REFERENCE:
        return {
          ...baseCondition,
          aiReference: '',
        };

      case AnswerOptionConditionType.DATABASE_COMPARISON:
        return {
          ...baseCondition,
          documentValueSource: 'aiExtraction',
          dbComparisonOperator: '==',
          dbComparisonExpected: 'match',
        };

      case AnswerOptionConditionType.QUESTION_ANSWER:
        return {
          ...baseCondition,
          questionAnswerComparison: 'equals',
        };

      default:
        console.warn(`Unknown condition type: ${type}`);
        return baseCondition;
    }
  } catch (error) {
    console.error('Error creating new condition:', error);
    throw new Error(`Kon geen nieuwe conditie van type ${type} aanmaken`);
  }
}

/**
 * Recursively count total conditions (not groups) in a tree
 */
export function countConditions(root: ConditionGroup | null): number {
  if (!root) {
    return 0;
  }

  try {
    let count = 0;

    for (const child of root.children) {
      if (isConditionGroup(child)) {
        count += countConditions(child);
      } else {
        count += 1;
      }
    }

    return count;
  } catch (error) {
    console.error('Error counting conditions:', error);
    return 0;
  }
}

/**
 * Metadata for each condition type
 */
export const CONDITION_TYPES: Array<{
  type: AnswerOptionConditionType;
  label: string;
  iconName: string;
  description: string;
}> = [
  {
    type: AnswerOptionConditionType.FILE_EXISTS,
    label: 'Bestand bestaat',
    iconName: 'File',
    description: 'Controleer of een bestand met het gelinkte label bestaat',
  },
  {
    type: AnswerOptionConditionType.EXPIRATION_DATE,
    label: 'Vervaldatum',
    iconName: 'Calendar',
    description: 'Controleer de vervaldatum van het document',
  },
  {
    type: AnswerOptionConditionType.FIELD_VALUE,
    label: 'Veldwaarde',
    iconName: 'ListChecks',
    description: 'Controleer een veld in het FileLog record',
  },
  {
    type: AnswerOptionConditionType.AI_QUESTION,
    label: 'AI Ja/Nee vraag',
    iconName: 'Robot',
    description: 'Stel een ja/nee vraag aan de AI over het document',
  },
  {
    type: AnswerOptionConditionType.AI_REFERENCE,
    label: 'AI Referentie',
    iconName: 'MagnifyingGlass',
    description: 'Laat de AI zoeken naar een specifieke referentie',
  },
  {
    type: AnswerOptionConditionType.DATABASE_COMPARISON,
    label: 'Database vergelijking',
    iconName: 'Database',
    description: 'Vergelijk een documentwaarde met een databaseveld',
  },
  {
    type: AnswerOptionConditionType.QUESTION_ANSWER,
    label: 'Vraag antwoord',
    iconName: 'GitBranch',
    description: 'Controleer het antwoord op een andere vraag',
  },
];

/**
 * FileLog fields that can be checked in FIELD_VALUE conditions
 */
export const FILELOG_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'status', label: 'Status' },
  { value: 'signStatus', label: 'Ondertekenstatus' },
  { value: 'expirationDate', label: 'Vervaldatum' },
  { value: 'documentDate', label: 'Document datum' },
];

/**
 * Enum values for each FileLog field
 */
export const FIELD_ENUM_VALUES: Record<
  string,
  Array<{ value: string; label: string }>
> = {
  status: [
    { value: 'Ok', label: 'Ok' },
    { value: 'Ok met opmerkingen', label: 'Ok met opmerkingen' },
    { value: 'Niet nodig', label: 'Niet nodig' },
    { value: 'Niet ok', label: 'Niet ok' },
    { value: 'Onbekend', label: 'Onbekend' },
  ],
  signStatus: [
    { value: 'Getekend', label: 'Getekend' },
    { value: 'Ontwerp', label: 'Ontwerp' },
    { value: 'Niet van toepassing', label: 'Niet van toepassing' },
    { value: 'Onbekend', label: 'Onbekend' },
  ],
};

/**
 * Database fields for DATABASE_COMPARISON (building + unit)
 */
export const DATABASE_FIELDS: Array<{
  category: string;
  fields: Array<{ key: string; label: string; description: string }>;
}> = [
  {
    category: 'Gebouw',
    fields: [
      {
        key: 'unitsCount',
        label: 'Aantal units',
        description: 'Totaal aantal units in het gebouw',
      },
      {
        key: 'constructionYear',
        label: 'Bouwjaar',
        description: 'Jaar van oplevering/bouw',
      },
      {
        key: 'buildingType',
        label: 'Gebouwtype',
        description: 'Type gebouw (Appartement, Kantoor, etc.)',
      },
      {
        key: 'googlePlaceLongAddress',
        label: 'Volledig adres',
        description: 'Compleet adres volgens Google Places',
      },
      {
        key: 'amountOfFloors',
        label: 'Aantal verdiepingen',
        description: 'Totaal aantal bouwlagen',
      },
      {
        key: 'nettoSurfaceInclCommonAreas',
        label: 'Netto opp. incl. gemeenschappelijke ruimtes',
        description: 'Totale netto oppervlakte inclusief gemeenschappelijke delen',
      },
      {
        key: 'nettoSurfaceExclCommonAreas',
        label: 'Netto opp. excl. gemeenschappelijke ruimtes',
        description: 'Totale netto oppervlakte exclusief gemeenschappelijke delen',
      },
      {
        key: 'plotSurface',
        label: 'Perceeloppervlakte',
        description: 'Oppervlakte van het perceel',
      },
      {
        key: 'numUnitsWithPermit',
        label: 'Aantal units met vergunning',
        description: 'Aantal units met geldige vergunning',
      },
      {
        key: 'numUnitsWithoutPermit',
        label: 'Aantal units zonder vergunning',
        description: 'Aantal units zonder geldige vergunning',
      },
      {
        key: 'permitStatus',
        label: 'Vergunningsstatus',
        description: 'Status van de bouwvergunning',
      },
      {
        key: 'heritageStatus',
        label: 'Erfgoedstatus',
        description: 'Beschermde status (monument, etc.)',
      },
      {
        key: 'mobiscore',
        label: 'Mobiscore',
        description: 'Bereikbaarheidsscore',
      },
      {
        key: 'acquisitionPrice',
        label: 'Aankoopprijs',
        description: 'Oorspronkelijke aankoopprijs',
      },
      {
        key: 'acquisitionDate',
        label: 'Aanschaf datum',
        description: 'Datum van aankoop',
      },
      {
        key: 'estimatedMarketValue',
        label: 'Geschatte marktwaarde',
        description: 'Huidige geschatte marktwaarde',
      },
      {
        key: 'strategicStatus',
        label: 'Strategische status',
        description: 'Status in portfolio strategie',
      },
      {
        key: 'ownershipType',
        label: 'Eigendomstype',
        description: 'Type eigendom (Volle eigendom, Erfpacht, etc.)',
      },
      {
        key: 'breeamRating',
        label: 'BREEAM rating',
        description: 'BREEAM duurzaamheidscertificaat',
      },
      {
        key: 'breeamScorePercent',
        label: 'BREEAM score (%)',
        description: 'BREEAM score in percentage',
      },
      {
        key: 'wellRating',
        label: 'WELL rating',
        description: 'WELL gezondheids- en welzijnscertificaat',
      },
      {
        key: 'wellScorePoints',
        label: 'WELL score (punten)',
        description: 'WELL score in punten',
      },
    ],
  },
  {
    category: 'Unit',
    fields: [
      {
        key: 'floor',
        label: 'Verdieping',
        description: 'Verdiepingsnummer',
      },
      {
        key: 'grossSurface',
        label: 'Bruto oppervlakte',
        description: 'Bruto vloeroppervlakte',
      },
      {
        key: 'netSurface',
        label: 'Netto oppervlakte',
        description: 'Netto vloeroppervlakte',
      },
      {
        key: 'unitType',
        label: 'Unit type',
        description: 'Type unit (Appartement, Studio, etc.)',
      },
      {
        key: 'unitSubtype',
        label: 'Unit subtype',
        description: 'Subtype van de unit',
      },
      {
        key: 'estimatedRentalValue',
        label: 'Geschatte huurwaarde',
        description: 'Geschatte maandelijkse huurprijs',
      },
      {
        key: 'estimatedCommonCosts',
        label: 'Geschatte gemeenschappelijke kosten',
        description: 'Geschatte maandelijkse gemeenschappelijke kosten',
      },
      {
        key: 'advertisedRentalPrice',
        label: 'Geadverteerde huurprijs',
        description: 'Gepubliceerde huurprijs',
      },
      {
        key: 'rentStatus',
        label: 'Verhuurstatus',
        description: 'Status van verhuur (Verhuurd, Leeg, etc.)',
      },
      {
        key: 'saleStatus',
        label: 'Verkoopstatus',
        description: 'Status van verkoop',
      },
      {
        key: 'outsideSpace',
        label: 'Buitenruimte',
        description: 'Type buitenruimte (Balkon, Terras, Tuin)',
      },
      {
        key: 'outsideSpaceSurface',
        label: 'Oppervlakte buitenruimte',
        description: 'Oppervlakte van de buitenruimte',
      },
      {
        key: 'furnished',
        label: 'Gemeubileerd',
        description: 'Of de unit gemeubileerd is',
      },
    ],
  },
];

/**
 * Database comparison operators
 */
export const DATABASE_COMPARISON_OPERATORS: Array<{
  value: string;
  label: string;
}> = [
  { value: '==', label: '==' },
  { value: '!=', label: '!=' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
];

/**
 * Database comparison expected outcomes
 */
export const DATABASE_COMPARISON_EXPECTED: Array<{
  value: string;
  label: string;
}> = [
  { value: 'match', label: 'Waarden komen overeen' },
  { value: 'mismatch', label: 'Waarden wijken af' },
  { value: 'document_value_exists', label: 'Document waarde bestaat' },
];
